import Image from 'next/image';
import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import ImageCarouselModal from '@/components/atomic/atoms/common/ImageCarouselModal';
import { getImageURL, getRankedImages } from '@/helpers/carousel';

const onImageClick = ({ image, openImageModal }) => {
  openImageModal(image);
};

const CustomThumbnail = ({ image, openImageModal }) => {
  const imageUrl = getImageURL(image);
  return (
    <div
      onClick={() =>
        onImageClick({
          image,
          openImageModal
        })
      }
    >
      <picture className='flex items-center'>
        <source
          data-srcset={image.url}
          type='image/jpg'
        />
        <Image
          src={imageUrl}
          width={76}
          height={50}
          alt='image thumbnail'
          style={{
            width: '76px',
            height: '50px',
            objectFit: 'cover'
          }}
        />
      </picture>
    </div>
  );
};

const CustomThumbnails = ({ images = [], openImageModal }) =>
  images.map((image, index) => (
    <CustomThumbnail
      key={index}
      {...{
        image,
        openImageModal
      }}
    />
  ));

const CarouselImage = ({
  containerStyle,
  excludeImageHeightFromContainer,
  height,
  image,
  layout,
  objectFit,
  objectPosition,
  openImageModal,
  preventOnCarouselImageClick,
  style,
  width
}) => {
  const className = excludeImageHeightFromContainer
    ? `relative overflow-hidden ${containerStyle}`
    : `relative ${height} overflow-hidden ${containerStyle}`;
  const imageUrl = getImageURL(image);
  return (
    <div
      className={className}
      key={`${image.url}`}
      onClick={() =>
        !preventOnCarouselImageClick &&
        onImageClick({
          image,
          openImageModal
        })
      }
    >
      <Image
        src={imageUrl}
        alt='image'
        className={style}
        fill={layout === 'fill'}
        width={
          layout !== 'fill'
            ? typeof width === 'number'
              ? width
              : parseInt(width, 10)
            : undefined
        }
        height={
          layout !== 'fill'
            ? typeof height === 'number'
              ? height
              : parseInt(height, 10)
            : undefined
        }
        style={{
          objectFit,
          objectPosition,
          width:
            layout !== 'fill'
              ? typeof width === 'number'
                ? `${width}px`
                : width
              : undefined,
          height:
            layout !== 'fill'
              ? typeof height === 'number'
                ? `${height}px`
                : height
              : undefined
        }}
      />
    </div>
  );
};

const ImageCarousel = ({
  containerStyle,
  excludeImageHeightFromContainer = false,
  height,
  images: imagesRaw = [],
  isThumbnail = true,
  layout,
  listing,
  objectFit,
  objectPosition,
  preventOnCarouselImageClick = false,
  style,
  width
}) => {
  const [isFocus, setIsFocus] = useState(false);
  const [slidePosition, setSlidePosition] = useState(0);

  const [modalImage, setModalImage] = useState(false);

  const openImageModal = (image) => {
    setModalImage(image);
  };

  const images = getRankedImages(imagesRaw);

  return (
    <div
      className='relative'
      onMouseEnter={() => {
        setIsFocus(true);
        setSlidePosition(1);
      }}
      onMouseLeave={() => {
        setIsFocus(false);
        setSlidePosition(0);
      }}
    >
      <Carousel
        {...{
          autoPlay: isFocus,
          infiniteLoop: true,
          interval: !listing ? 5000 : 1500,
          selectedItem: slidePosition,
          showArrows: false,
          showIndicators: images.length > 1,
          showStatus: false,
          showThumbs: !isMobile && !listing,
          transitionTime: 800,
          renderThumbs: () =>
            isThumbnail && CustomThumbnails({ images, openImageModal })
        }}
      >
        {images.map((image, index) => (
          <CarouselImage
            key={index}
            {...{
              containerStyle,
              excludeImageHeightFromContainer,
              height,
              image,
              layout,
              objectFit,
              objectPosition,
              openImageModal,
              preventOnCarouselImageClick,
              style,
              width
            }}
          />
        ))}
      </Carousel>
      {modalImage && (
        <ImageCarouselModal
          {...{
            images,
            modalImage,
            openImageModal
          }}
        />
      )}
    </div>
  );
};

export default ImageCarousel;
